package com.sweetlime.dungeonamigo.presentation.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.util.AvatarMapper

/**
 * Bottom sheet shown on the Review step of character creation (Amigo+ only).
 * Lets the user choose one of the 6 presentation variants for their character's
 * race/class combination.
 *
 * Free-tier characters always use the default (no variant suffix). This sheet
 * is only rendered when the user is on the "plus" tier.
 *
 * @param race              Character race key, e.g. "Human".
 * @param characterClass    Character class key, e.g. "Wizard".
 * @param currentAvatarId   The full avatarId currently stored on the draft.
 * @param onSave            Called with the updated full avatarId (with variant suffix).
 * @param onDismiss         Called when the sheet closes without saving.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AvatarVariantPickerSheet(
    race: String,
    characterClass: String,
    currentAvatarId: String,
    onSave: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    val baseId = AvatarMapper.avatarId(race, characterClass)
    val currentVariant = AvatarMapper.variantOf(currentAvatarId)
    var selectedVariant by remember { mutableStateOf(currentVariant) }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        containerColor = BrandColors.Surface,
        dragHandle = { BottomSheetDefaults.DragHandle(color = BrandColors.TextSecondary) },
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 20.dp)
                .padding(bottom = 32.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            // ── Header ────────────────────────────────────────────────────────
            Column {
                Text(
                    text = "Choose Variant",
                    color = BrandColors.Parchment,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                )
                Text(
                    text = "${AvatarMapper.raceDisplayNames[AvatarMapper.raceKey(race)] ?: race} · $characterClass",
                    color = BrandColors.TextSecondary,
                    fontSize = 13.sp,
                )
            }

            // ── 2-column variant grid ─────────────────────────────────────────
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                AvatarMapper.variants.chunked(2).forEach { row ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        row.forEach { variant ->
                            val variantAvatarId = if (variant.isBlank()) baseId else "${baseId}_${variant}"
                            val isSelected = variant == selectedVariant
                            VariantTile(
                                avatarId = variantAvatarId,
                                label = AvatarMapper.variantLabels[variant] ?: variant,
                                isSelected = isSelected,
                                primaryColor = BrandColors.Parchment,
                                secondaryColor = BrandColors.SpellBlue,
                                onClick = { selectedVariant = variant },
                                modifier = Modifier.weight(1f),
                            )
                        }
                    }
                }
            }

            // ── Actions ───────────────────────────────────────────────────────
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                OutlinedButton(
                    onClick = onDismiss,
                    modifier = Modifier.weight(1f),
                    border = androidx.compose.foundation.BorderStroke(1.dp, BrandColors.SurfaceLight),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.TextSecondary),
                    shape = RoundedCornerShape(8.dp),
                ) {
                    Text("Cancel")
                }
                Button(
                    onClick = {
                        val finalId = if (selectedVariant.isBlank()) baseId else "${baseId}_${selectedVariant}"
                        onSave(finalId)
                    },
                    modifier = Modifier.weight(1f),
                    colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                    shape = RoundedCornerShape(8.dp),
                ) {
                    Text("Save", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

@Composable
private fun VariantTile(
    avatarId: String,
    label: String,
    isSelected: Boolean,
    primaryColor: Color,
    secondaryColor: Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(10.dp))
            .clickable(onClick = onClick)
            .then(
                if (isSelected)
                    Modifier.border(2.dp, BrandColors.Ember, RoundedCornerShape(10.dp))
                else
                    Modifier.border(1.dp, BrandColors.SurfaceLight, RoundedCornerShape(10.dp))
            )
            .background(BrandColors.SurfaceLight.copy(alpha = 0.3f))
            .padding(bottom = 8.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        CharacterPortrait(
            characterClass = avatarId.substringAfterLast("_"),
            avatarId = avatarId,
            primaryColor = primaryColor,
            secondaryColor = secondaryColor,
            size = PortraitSize.LARGE,
            modifier = Modifier.fillMaxWidth(),
        )
        Text(
            text = label,
            color = if (isSelected) BrandColors.Ember else BrandColors.TextSecondary,
            fontSize = 12.sp,
            fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(top = 4.dp),
        )
    }
}
